/**
 * PATH: packages/ui/src/mobile/MobileActionSheet.tsx
 * FILE_ID: UI-MOBILE-ACTION-SHEET-001
 * PURPOSE: Full-width bottom-anchored action sheet for mobile context menus.
 * WHY: Touch long-press needs a thumb-reachable sheet, never a floating
 *      mid-screen popover. Shared so quote-builder / CRM can reuse the same
 *      chrome without forking job-hub Schedule's local sheet.
 */
import type { ComponentType } from 'react'

import { cn } from '../lib/utils'
import { MobileSheet } from './MobileSheet'

export interface MobileActionSheetItem {
  id: string
  label: string
  icon?: ComponentType<{ className?: string }>
  destructive?: boolean
  disabled?: boolean
  onSelect: () => void
}

export interface MobileActionSheetProps {
  open: boolean
  onClose: () => void
  title?: string
  items: MobileActionSheetItem[]
}

export function MobileActionSheet({
  open,
  onClose,
  title,
  items,
}: MobileActionSheetProps) {
  return (
    <MobileSheet
      open={open}
      onClose={onClose}
      title={title}
      rootTestId="mobile-action-sheet-root"
      sheetTestId="mobile-action-sheet"
    >
      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-1"
        role="menu"
        aria-label={title ?? 'Actions'}
      >
        {items.length === 0 ? (
          <p className="px-2 py-4 text-center text-sm text-neutral-500">
            No actions available.
          </p>
        ) : (
          items.map((item) => {
            const Icon = item.icon
            return (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  if (item.disabled) return
                  onClose()
                  requestAnimationFrame(() => item.onSelect())
                }}
                className={cn(
                  'flex w-full min-h-[44px] items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold transition-colors',
                  item.disabled
                    ? 'cursor-not-allowed text-neutral-400'
                    : item.destructive
                      ? 'text-red-600 hover:bg-red-50 active:bg-red-100 dark:text-red-400 dark:hover:bg-red-950/40'
                      : 'text-neutral-900 hover:bg-neutral-100 active:bg-neutral-200 dark:text-neutral-100 dark:hover:bg-neutral-900',
                )}
              >
                {Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
              </button>
            )
          })
        )}
      </div>
    </MobileSheet>
  )
}
